import { demoFunc } from './dees-harness-session-list.demo.js';
import {
  DeesElement,
  html,
  css,
  customElement,
  type TemplateResult,
  property,
  state,
  cssManager,
} from '@design.estate/dees-element';
import { themeDefaultStyles } from '../../00theme.js';
import type { IHarnessSessionMeta } from '../interfaces.js';
import { harnessFormatRelativeTime } from '../harness.helpers.js';
import '../../00group-utility/dees-icon/dees-icon.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-harness-session-list': DeesHarnessSessionList;
  }
}

/**
 * Searchable conversation list, embeddable in a sidebar or a DeesModal.
 * Emits `harness-session-select` on click and `harness-session-open` on
 * double-click or Enter.
 */
@customElement('dees-harness-session-list')
export class DeesHarnessSessionList extends DeesElement {
  public static demo = demoFunc;
  public static demoGroups = ['Agent Chat'];

  @property({ attribute: false })
  accessor sessions: IHarnessSessionMeta[] = [];

  @property()
  accessor selectedSessionId: string = '';

  @property({ type: Boolean })
  accessor loading: boolean = false;

  @property()
  accessor emptyText: string = 'No conversations yet.';

  @state()
  accessor search: string = '';

  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        font-family: var(--dees-font-family);
      }

      .searchRow {
        flex: 0 0 auto;
        padding: var(--dees-spacing-sm);
      }

      .searchRow input {
        width: 100%;
        height: var(--dees-control-height-sm);
        padding: 0 var(--dees-spacing-md);
        border: 1px solid var(--dees-color-border-default);
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: var(--dees-color-bg-primary);
        color: var(--dees-color-text-primary);
        font-family: inherit;
        font-size: var(--dees-font-control-size, 13px);
        outline: none;
        box-sizing: border-box;
      }

      .searchRow input:focus {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      .list {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        scrollbar-width: thin;
        scrollbar-color: var(--dees-color-scrollbar-thumb) transparent;
        padding: 0 var(--dees-spacing-xs) var(--dees-spacing-xs);
        display: grid;
        gap: 2px;
        align-content: start;
      }

      .item {
        width: 100%;
        display: block;
        padding: var(--dees-spacing-xs) var(--dees-spacing-sm);
        border: 0;
        border-radius: var(--dees-radius-md);
        corner-shape: var(--dees-corner-shape);
        background: transparent;
        color: var(--dees-color-text-primary);
        cursor: pointer;
        font-family: inherit;
        text-align: left;
        transition: background var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .item:hover {
        background: var(--dees-color-hover);
      }

      .item.selected {
        background: var(--dees-color-active);
        box-shadow: inset 2px 0 0 var(--dees-color-accent-primary);
      }

      .itemTitle {
        display: flex;
        align-items: baseline;
        gap: var(--dees-spacing-sm);
        font-size: var(--dees-font-control-size-sm, 12px);
        font-weight: 500;
      }

      .itemTitle .titleText {
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .itemTime {
        margin-left: auto;
        flex: 0 0 auto;
        color: var(--dees-color-text-muted);
        font-size: var(--dees-font-caption2-size, 10px);
      }

      .itemPreview {
        margin-top: 1px;
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-caption1-size, 11px);
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .emptyNote {
        padding: var(--dees-spacing-lg);
        color: var(--dees-color-text-secondary);
        font-size: var(--dees-font-control-size-sm, 12px);
        text-align: center;
      }
    `,
  ];

  private get filteredSessions(): IHarnessSessionMeta[] {
    const query = this.search.trim().toLowerCase();
    const sorted = [...this.sessions].sort(
      (left, right) => (right.updatedAt ?? right.createdAt ?? 0) - (left.updatedAt ?? left.createdAt ?? 0),
    );
    if (!query) return sorted;
    return sorted.filter((session) =>
      [session.title, session.preview, session.id].filter(Boolean).join(' ').toLowerCase().includes(query),
    );
  }

  public focusSearch(): void {
    this.shadowRoot?.querySelector<HTMLInputElement>('.searchRow input')?.focus();
  }

  public render(): TemplateResult {
    const sessions = this.filteredSessions;
    return html`
      <div class="searchRow">
        <input
          placeholder="Search conversations"
          .value=${this.search}
          @input=${(event: Event) => {
            this.search = (event.currentTarget as HTMLInputElement).value;
          }}
        />
      </div>
      <div class="list">
        ${this.loading
          ? html`<div class="emptyNote">Loading conversations…</div>`
          : sessions.length
            ? sessions.map((session) => this.renderItem(session))
            : html`<div class="emptyNote">${this.sessions.length ? 'No conversations match this search.' : this.emptyText}</div>`}
      </div>
    `;
  }

  private renderItem(session: IHarnessSessionMeta): TemplateResult {
    return html`
      <button
        class="item ${session.id === this.selectedSessionId ? 'selected' : ''}"
        type="button"
        @click=${() => this.emit('harness-session-select', session)}
        @dblclick=${() => this.emit('harness-session-open', session)}
        @keydown=${(event: KeyboardEvent) => {
          if (event.key === 'Enter') this.emit('harness-session-open', session);
        }}
      >
        <span class="itemTitle">
          <span class="titleText">${session.title || session.id}</span>
          <span class="itemTime">${harnessFormatRelativeTime(session.updatedAt ?? session.createdAt)}</span>
        </span>
        ${session.preview
          ? html`<span class="itemPreview">${session.preview}${session.messageCount ? ` · ${session.messageCount} messages` : ''}</span>`
          : ''}
      </button>
    `;
  }

  private emit(name: string, session: IHarnessSessionMeta): void {
    this.dispatchEvent(new CustomEvent(name, { detail: { session }, bubbles: true, composed: true }));
  }
}
